import { useQuery } from '@tanstack/react-query';

import { BadgeCell } from '@/components/table/cells';
import { useAuth } from '@/lib/auth/auth-context';
import { Field, Fields, Panel, TabSkeleton } from '@/components/panel/panels';
import { TabError } from '@/screens/user-detail/TabError';
import { formatDateTime } from '@/lib/format';

import { planCronQueryOptions } from './plan-ops-query';
import { formatCountdown } from './plan-run-tone';

/**
 * The re-plan schedule, read from BullMQ rather than from a constant.
 *
 * It sits above the run list because the first question this screen answers is
 * not "which run failed" but "is the thing that produces runs still running at
 * all" — a cron that stopped firing looks, from the list alone, exactly like a
 * quiet week.
 */
export function CronPanel() {
  const { api } = useAuth();
  const query = useQuery(planCronQueryOptions(api));

  const cron = query.data;
  const scheduled = cron !== undefined && cron.pattern !== null;

  return (
    <Panel
      title="Weekly re-plan"
      action={
        cron === undefined ? null : (
          <BadgeCell
            label={cron.isPaused ? 'paused' : scheduled ? 'scheduled' : 'not scheduled'}
            tone={cron.isPaused ? 'warn' : scheduled ? 'ok' : 'err'}
            dot
          />
        )
      }
    >
      {query.isPending ? <TabSkeleton rows={1} /> : null}
      {query.isError ? <TabError message={query.error.message} onRetry={() => void query.refetch()} /> : null}

      {cron === undefined ? null : (
        <div className="flex flex-col gap-3">
          <Fields>
            <Field label="Queue" value={cron.queue} mono />
            <Field label="Pattern" value={cron.pattern} mono />
            <Field
              label="Next run"
              value={
                cron.nextRunAt === null
                  ? null
                  : `${formatDateTime(cron.nextRunAt)} (${formatCountdown(cron.nextRunAt)})`
              }
            />
            <Field label="Waiting" value={String(cron.counts.waiting + cron.counts.delayed)} />
            <Field label="Failed" value={String(cron.counts.failed)} />
          </Fields>

          {scheduled ? null : (
            <p role="note" className="text-[13px] leading-relaxed text-err-fg">
              No repeatable job is registered on this queue, so nobody is generating next week’s plans.
            </p>
          )}
        </div>
      )}
    </Panel>
  );
}
